import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/EnergyField';
import { EvidenceLabel } from '../components/EvidenceLabel';
import { domainImage } from '../lib/domainImages';
import { ArrowLeft, ArrowRight, Droplets, AlertTriangle, GitBranch, BookMarked, Compass } from 'lucide-react';

const rise = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } };

// Curated comparison — figures kept as qualitative classes / ranges (they vary by
// product and source water). Grounded in WHO HWT scheme, EPA drinking-water
// treatment guidance, CDC, and Eawag/SODIS field literature.
const METHODS = [
  {
    name: 'Sediment / Pre-filtration', evidence: 'ESTABLISHED',
    purpose: 'Removes suspended particles & turbidity; protects downstream stages.',
    addresses: 'Silt, sand, rust, visible particulates', notAddressed: 'Microbes, dissolved chemicals, salts',
    flow: 'High', power: 'None (gravity/line pressure)', consumables: 'Cartridge / mesh',
    maintenance: 'Rinse or swap when flow drops', cost: 'Low',
    failure: 'Clogging, channeling, bypass if not seated',
  },
  {
    name: 'Activated Carbon (GAC)', evidence: 'ESTABLISHED',
    purpose: 'Adsorbs taste/odour, chlorine, many organics & some VOCs.',
    addresses: 'Chlorine, VOCs, taste/odour, some pesticides', notAddressed: 'Microbes, salts, nitrate, most heavy metals',
    flow: 'Medium–High', power: 'None', consumables: 'Carbon cartridge',
    maintenance: 'Replace on schedule (capacity is finite)', cost: 'Low–Medium',
    failure: 'Breakthrough when saturated; biofilm regrowth if left wet & unused',
  },
  {
    name: 'Ceramic Filtration', evidence: 'SUPPORTED',
    purpose: 'Size-exclusion (often silver-impregnated) for bacteria & protozoa.',
    addresses: 'Bacteria, protozoa, turbidity', notAddressed: 'Viruses (not reliably), dissolved chemicals/salts',
    flow: 'Low', power: 'None (gravity)', consumables: 'Ceramic element',
    maintenance: 'Scrub surface; replace if cracked', cost: 'Low–Medium',
    failure: 'Hairline cracks bypass the barrier; slow flow when fouled',
  },
  {
    name: 'Hollow-fibre / Ultrafiltration', evidence: 'SUPPORTED',
    purpose: 'Membrane size-exclusion (~0.01–0.02 µm) barrier.',
    addresses: 'Bacteria, protozoa, most viruses (partial), turbidity', notAddressed: 'Dissolved salts, most chemicals',
    flow: 'Medium', power: 'None–Low', consumables: 'Membrane module',
    maintenance: 'Backflush; keep from freezing/drying', cost: 'Medium',
    failure: 'Fibre damage from freezing; irreversible fouling; virus passage at larger pore ratings',
  },
  {
    name: 'Reverse Osmosis (RO)', evidence: 'ESTABLISHED',
    purpose: 'High-pressure membrane removing dissolved solids & most contaminants.',
    addresses: 'Salts, nitrate, arsenic, fluoride, many metals, microbes', notAddressed: 'Dissolved gases; strips beneficial minerals',
    flow: 'Low', power: 'Pressure (pump/line; energy-intensive)', consumables: 'RO membrane + pre/post filters',
    maintenance: 'Change pre-filters; sanitise; monitor TDS', cost: 'Medium–High',
    failure: 'Membrane fouling/scaling; 15–30% reject water; needs pre-treatment',
  },
  {
    name: 'UV Disinfection', evidence: 'ESTABLISHED',
    purpose: 'UV-C dose inactivates pathogens without chemicals.',
    addresses: 'Bacteria, viruses, protozoa (in clear water)', notAddressed: 'Turbidity, chemicals, no residual protection',
    flow: 'Medium–High', power: 'Electric (lamp)', consumables: 'UV lamp (annual), sleeve',
    maintenance: 'Clean sleeve; replace lamp; pre-filter turbidity', cost: 'Medium',
    failure: 'Turbidity shields pathogens; aged lamp under-doses; no protection after the point of treatment',
  },
  {
    name: 'Boiling / Thermal', evidence: 'ESTABLISHED',
    purpose: 'Heat inactivation — the most robust field option for pathogens.',
    addresses: 'Bacteria, viruses, protozoa (all)', notAddressed: 'Chemicals, salts, turbidity',
    flow: 'Batch', power: 'Fuel / heat', consumables: 'Fuel',
    maintenance: 'None', cost: 'Low (equipment) · fuel-dependent',
    failure: 'Fuel intensive; recontamination on storage; does not remove chemistry (WHO/CDC: rolling boil ≥1 min, ≥3 min at high altitude)',
  },
  {
    name: 'Chlorination', evidence: 'ESTABLISHED',
    purpose: 'Chemical disinfection with a protective residual.',
    addresses: 'Bacteria, viruses; residual guards against recontamination', notAddressed: 'Cryptosporidium (poorly), turbidity, chemicals',
    flow: 'Batch–Continuous', power: 'None', consumables: 'Chlorine (liquid/tablet)',
    maintenance: 'Dose to contact time; store chemical correctly', cost: 'Low',
    failure: 'Under-dosing in turbid water; disinfection by-products; taste; weak vs protozoal cysts',
  },
  {
    name: 'Solar Disinfection (SODIS)', evidence: 'OBSERVATIONAL',
    purpose: 'UV-A + heat in clear PET bottles under sunlight.',
    addresses: 'Bacteria, protozoa, some viruses', notAddressed: 'Chemicals, salts, high turbidity',
    flow: 'Batch (slow)', power: 'Sunlight', consumables: 'Clear bottles',
    maintenance: 'Refill/replace bottles; ensure clarity', cost: 'Very low',
    failure: 'Needs ≥6 h strong sun; fails above ~26–30 NTU turbidity; regrowth if stored warm; interim measure',
  },
  {
    name: 'Biosand Filter', evidence: 'OBSERVATIONAL',
    purpose: 'Slow sand filtration with a biological schmutzdecke layer.',
    addresses: 'Turbidity, protozoa, partial bacteria (LRV ~1–2)', notAddressed: 'Viruses, dissolved chemicals/salts',
    flow: 'Low', power: 'None (gravity)', consumables: 'Sand media',
    maintenance: 'Preserve biolayer; do not run dry', cost: 'Low',
    failure: 'Variable field performance; immature biolayer under-performs; disrupted by intermittent use',
  },
  {
    name: 'Coagulation–Flocculation', evidence: 'SUPPORTED',
    purpose: 'Aggregates fine particles for settling; often paired with disinfection.',
    addresses: 'Turbidity, some metals & pathogens (combined sachets)', notAddressed: 'Most dissolved chemicals alone',
    flow: 'Batch', power: 'None (stir + settle)', consumables: 'Coagulant / combined sachet',
    maintenance: 'Correct dose; decant carefully', cost: 'Low',
    failure: 'Wrong dose leaves haze or floc carryover; multi-step user error',
  },
  {
    name: 'Gravity Multi-stage', evidence: 'SUPPORTED',
    purpose: 'Combined stages (e.g. ceramic + carbon) with no power required.',
    addresses: 'Turbidity, bacteria/protozoa, taste/odour', notAddressed: 'Dissolved salts; virus removal depends on stage rating',
    flow: 'Low–Medium', power: 'None (gravity)', consumables: 'Element set',
    maintenance: 'Clean/replace elements per stage', cost: 'Medium',
    failure: 'Weakest stage caps performance; cracked ceramic bypass; slow when fouled',
  },
];

const FAILURE_MODES = [
  'Clogging & channeling', 'Contaminant breakthrough', 'Membrane fouling', 'Microbial regrowth',
  'Inadequate contact time', 'Poor pre-filtration', 'Freezing damage', 'Contaminated storage',
  'Mineral scaling', 'UV turbidity shielding', 'Lamp / media ageing', 'User / dosing error',
];

const DECISIONS = [
  { q: 'Microbial contamination, no power available', a: 'Boiling (most robust), chlorination for residual protection, or a hollow-fibre/ceramic barrier. SODIS only as an interim measure in clear water.' },
  { q: 'Dissolved salts, arsenic, nitrate or fluoride are the concern', a: 'Reverse osmosis is the practical option; most filters do not remove dissolved ions. Confirm with a water test first.' },
  { q: 'Water is visibly turbid', a: 'Pre-filter or coagulate/settle before any disinfection — turbidity shields pathogens from UV and chlorine and clogs membranes.' },
  { q: 'System must run seasonally in freezing climates', a: 'Protect membranes and ceramics from freezing; favour boiling or chemical methods that survive cold storage.' },
  { q: 'Taste, odour or chlorine from treated municipal water', a: 'Activated carbon is sufficient; it does not disinfect, so keep it downstream of any pathogen barrier.' },
];

const SOURCES = [
  { label: 'WHO — International Scheme to Evaluate Household Water Treatment Technologies (log-reduction tiers)', url: 'https://www.who.int/tools/international-scheme-to-evaluate-household-water-treatment-technologies' },
  { label: 'WHO — Guidelines for Drinking-water Quality', url: 'https://www.who.int/publications/i/item/9789241549950' },
  { label: 'US EPA — Overview of Drinking Water Treatment Technologies', url: 'https://www.epa.gov/sdwa/overview-drinking-water-treatment-technologies' },
  { label: 'CDC — Making Water Safe in an Emergency (boiling & disinfection)', url: 'https://www.cdc.gov/healthywater/emergency/making-water-safe.html' },
  { label: 'Eawag / SANDEC — SODIS reference manual', url: 'https://www.eawag.ch/en/department/sandec/' },
];

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <span className="mono-meta text-[#5b626f]">{label}</span>
    <span className="font-sans-tech text-sm text-[#C8CDD6] leading-snug">{children}</span>
  </div>
);

const WaterFieldGuide = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9]">
      <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')}><Logo /></button>
          <button onClick={() => navigate(-1)} className="btn-quiet !py-2 !px-5" data-testid="guide-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        </div>
      </nav>

      <div className="relative w-full h-60 lg:h-80 overflow-hidden border-b border-[#262A36]" data-testid="guide-hero">
        <img src={domainImage('Water Systems')} alt="Off-grid water systems" className="w-full h-full object-cover opacity-[0.5] grayscale-[0.2]" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/40 via-[#08080A]/40 to-[#08080A]" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container mx-auto px-6 lg:px-12 pb-6 max-w-5xl">
            <p className="mono-meta text-[#26B0A8] flex items-center gap-2"><Droplets className="w-4 h-4" /> Field Guide 001 · Open Resource · Source Synthesis</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 py-14 max-w-5xl" data-testid="water-field-guide">
        <motion.div {...rise} className="mb-14 max-w-3xl">
          <h1 className="font-serif-display text-4xl lg:text-6xl font-light leading-[1.06] mb-6">Off-Grid Water Systems<br /><span className="gradient-text italic">Field Guide 001</span></h1>
          <p className="font-sans-tech text-lg text-[#949BAA] leading-relaxed mb-5">
            Twelve approaches compared across purpose, contaminant coverage, flow, power, maintenance,
            approximate cost, common failure modes, and strength of evidence.
          </p>
          <div className="glass-panel rounded-xl px-6 py-5 space-y-2">
            <p className="mono-meta text-[#5b626f]">Scope</p>
            <p className="font-sans-tech text-sm text-[#C8CDD6] leading-relaxed">
              For makers, off-grid households, and field researchers choosing between treatment methods.
              It is a <span className="text-[#8a93a3]">source synthesis</span> of public engineering and health
              guidance — not medical advice and not original FreeFieldFlow testing. It does <span className="text-[#8a93a3]">not</span> claim
              exact removal figures (these vary by product and source water) and does not guarantee safety. Test your
              water and match the method to the actual contaminant.
            </p>
          </div>
        </motion.div>

        {/* COMPARISON MATRIX */}
        <motion.div {...rise} className="mb-16">
          <p className="mono-label mb-3">Comparison</p>
          <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-8">System comparison matrix</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="guide-matrix">
            {METHODS.map((m) => (
              <div key={m.name} className="glass-panel rounded-xl p-6 flex flex-col" data-testid={`method-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-serif-display text-xl font-medium leading-snug">{m.name}</h3>
                  <EvidenceLabel evidenceClass={m.evidence} className="shrink-0 mt-1" />
                </div>
                <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed mb-4">{m.purpose}</p>
                <div className="grid grid-cols-2 gap-x-5 gap-y-3 border-t border-[#262A36]/60 pt-4">
                  <Field label="Addresses">{m.addresses}</Field>
                  <Field label="Not reliably">{m.notAddressed}</Field>
                  <Field label="Flow">{m.flow}</Field>
                  <Field label="Power">{m.power}</Field>
                  <Field label="Consumables">{m.consumables}</Field>
                  <Field label="Maintenance">{m.maintenance}</Field>
                  <Field label="Approx. cost">{m.cost}</Field>
                  <Field label="Failure modes">{m.failure}</Field>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAILURE MODES */}
        <motion.div {...rise} className="mb-16">
          <p className="mono-label mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Practical reality</p>
          <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-4">Why systems fail in the field</h2>
          <p className="font-sans-tech text-[#949BAA] leading-relaxed mb-6 max-w-3xl">Most real-world failures are not the technology — they are maintenance, pre-treatment, and storage. Watch for:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="guide-failures">
            {FAILURE_MODES.map((f) => (
              <div key={f} className="glass-panel rounded-lg px-4 py-3 border-l-2 border-[#7a6f4a]/50">
                <span className="font-sans-tech text-sm text-[#C8CDD6]">{f}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* DECISION PATHS */}
        <motion.div {...rise} className="mb-16">
          <p className="mono-label mb-3 flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Choosing</p>
          <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-8">Decision paths</h2>
          <div className="space-y-4" data-testid="guide-decisions">
            {DECISIONS.map((d, i) => (
              <div key={i} className="glass-panel rounded-xl px-6 py-5">
                <p className="font-sans-tech text-[#E2E4E9] mb-2">If — {d.q}</p>
                <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed">→ {d.a}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* SOURCES */}
        <motion.div {...rise} className="mb-16">
          <p className="mono-label mb-3 flex items-center gap-2"><BookMarked className="w-3.5 h-3.5" /> Traceable</p>
          <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-6">Sources</h2>
          <ul className="space-y-2" data-testid="guide-sources">
            {SOURCES.map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="py-2.5 flex items-start gap-3 group" data-testid={`guide-source-${i}`}>
                  <span className="mono-meta pt-0.5">[{i + 1}]</span>
                  <span className="font-sans-tech text-sm text-[#949BAA] group-hover:text-[#26B0A8] transition-colors">{s.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div {...rise} className="glass-hud rounded-2xl p-8 lg:p-10 reflect-top scanlines">
          <p className="mono-label mb-3 flex items-center gap-2"><GitBranch className="w-3.5 h-3.5 text-[#26B0A8]" /> Go deeper</p>
          <h3 className="font-serif-display text-3xl font-light mb-4">The archive continues past this guide</h3>
          <p className="font-sans-tech text-[#949BAA] mb-7 max-w-2xl leading-relaxed">
            Research Access contains the deeper archive: system blueprints, comparative technical notes, component
            references, field observations, and the connected source records behind each method — every record carrying
            a visible evidence class.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={() => navigate('/dashboard')} className="btn-primary" data-testid="guide-cta-access">Explore Research Access <ArrowRight className="w-4 h-4" /></button>
            <button onClick={() => navigate('/method')} className="btn-ambient" data-testid="guide-cta-method">How we classify evidence</button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WaterFieldGuide;
